// ============================================================================
// components/remote/useRemoteFiles.ts — Ф3: ФАЙЛЫ УСТРОЙСТВА.
// Host: по разрешению («Разрешить файлы») выполняет запросы через Wails-мост
// (HostFsRoots/List/Read/Write/Delete) и отвечает чанками по data-каналу.
// Viewer: запрашивает доступ, листает каталоги, скачивает/загружает/удаляет.
// Контракт: plans/remote-protocol-contract.md §6. Всё в журнал регистрации.
// ============================================================================

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { logEvent } from '@/lib/eventLog';
import { decodeRemoteData, encodeRemoteData } from '@/lib/remote/remoteSession';

const CHUNK_SIZE = 12 * 1024;
const REQUEST_TIMEOUT_MS = 60_000;

interface PendingReq {
  resolve: (v: Record<string, unknown>) => void;
  timer: number;
  chunks?: { total: number; parts: string[] };
}

interface PutBuffer {
  path: string;
  total: number;
  parts: string[];
}

interface Options {
  roomRef: MutableRefObject<Room | null>;
  role: 'viewer' | 'host';
  sessionId: string;
  active: boolean;
}

export function useRemoteFiles({ roomRef, role, sessionId, active }: Options) {
  const [filesOn, setFilesOn] = useState(false);
  const [filesRequested, setFilesRequested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const filesOnRef = useRef(false);
  const pendingRef = useRef<Map<string, PendingReq>>(new Map());
  const putRef = useRef<Map<string, PutBuffer>>(new Map());
  useEffect(() => {
    filesOnRef.current = filesOn;
  }, [filesOn]);

  const log = useCallback((level: 'info' | 'error', message: string, details?: string) => {
    void logEvent({
      level,
      category: 'ui',
      message,
      entityType: 'remote',
      entityCode: 'session',
      objectId: sessionId,
      details: details || '',
    });
  }, [sessionId]);

  const publish = useCallback((obj: unknown) => {
    const room = roomRef.current;
    if (!room) return;
    room.localParticipant
      .publishData(encodeRemoteData(obj), { reliable: true })
      .catch((e: unknown) => log('error', 'удалённый доступ: не удалось отправить данные файлов', String((e as Error)?.message || e)));
  }, [roomRef, log]);

  // HOST: запросы от viewer'а
  useEffect(() => {
    if (!active || role !== 'host') return;
    const room = roomRef.current;
    if (!room) return;
    const bridge = () => window.go?.main?.App;

    const deny = (id: string) => publish({ t: 'file_resp', id, ok: false, error: 'доступ к файлам не разрешён хостом' });

    const onData = (payload: Uint8Array) => {
      const msg = decodeRemoteData(payload);
      if (!msg) return;
      if (msg.t === 'file_ask') {
        setFilesRequested(true);
        log('info', 'удалённый доступ: запрошен доступ к файлам устройства');
        return;
      }
      if (msg.t === 'file_req') {
        const id = String(msg.id || '');
        if (!id) return;
        if (!filesOnRef.current) {
          deny(id);
          return;
        }
        const app = bridge();
        if (!app?.HostFsList) {
          publish({ t: 'file_resp', id, ok: false, error: 'файлы устройства доступны только на desktop-хосте' });
          return;
        }
        const op = String(msg.op || '');
        const path = String(msg.path || '');
        if (op === 'roots') {
          app.HostFsRoots().then((r) => publish({ t: 'file_resp', id, ok: r.ok, error: r.error, roots: r.roots }));
        } else if (op === 'list') {
          app.HostFsList(path).then((r) => publish({ t: 'file_resp', id, ok: r.ok, error: r.error, path: r.path, parent: r.parent, entries: r.entries }));
        } else if (op === 'get') {
          app.HostFsRead(path).then((r) => {
            if (!r.ok) {
              publish({ t: 'file_resp', id, ok: false, error: r.error });
              return;
            }
            const b64 = String(r.data || '');
            const total = Math.max(1, Math.ceil(b64.length / CHUNK_SIZE));
            for (let seq = 0; seq < total; seq++) {
              publish({
                t: 'file_resp',
                id,
                ok: true,
                name: r.name,
                size: r.size,
                seq,
                total,
                data: b64.slice(seq * CHUNK_SIZE, (seq + 1) * CHUNK_SIZE),
              });
            }
            log('info', 'удалённый доступ: файл отправлен зрителю', JSON.stringify({ path, size: r.size }));
          });
        } else if (op === 'put') {
          putRef.current.set(id, { path, total: Number(msg.total || 0), parts: [] });
        } else if (op === 'del') {
          app.HostFsDelete(path).then((r) => {
            publish({ t: 'file_resp', id, ok: r.ok, error: r.error });
            if (r.ok) log('info', 'удалённый доступ: файл удалён на хосте', path);
          });
        } else {
          publish({ t: 'file_resp', id, ok: false, error: `неизвестная операция: ${op}` });
        }
        return;
      }
      if (msg.t === 'file_chunk') {
        const id = String(msg.id || '');
        const buf = putRef.current.get(id);
        if (!buf) return;
        buf.parts[Number(msg.seq)] = String(msg.data || '');
        const received = buf.parts.filter((p) => typeof p === 'string').length;
        if (received >= buf.total) {
          putRef.current.delete(id);
          const app = bridge();
          if (!app?.HostFsWrite) {
            publish({ t: 'file_resp', id, ok: false, error: 'запись доступна только на desktop-хосте' });
            return;
          }
          app.HostFsWrite(buf.path, buf.parts.join('')).then((r) => {
            publish({ t: 'file_resp', id, ok: r.ok, error: r.error, size: r.size });
            if (r.ok) log('info', 'удалённый доступ: файл принят на хосте', JSON.stringify({ path: buf.path, size: r.size }));
          });
        }
      }
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [active, role, roomRef, publish, log]);

  // VIEWER: ответы хоста + состояние доступа
  useEffect(() => {
    if (!active || role !== 'viewer') return;
    const room = roomRef.current;
    if (!room) return;
    const onData = (payload: Uint8Array) => {
      const msg = decodeRemoteData(payload);
      if (!msg) return;
      if (msg.t === 'files') {
        setFilesOn(!!msg.on);
        return;
      }
      if (msg.t !== 'file_resp') return;
      const id = String(msg.id || '');
      const pending = pendingRef.current.get(id);
      if (!pending) return;
      const done = (v: Record<string, unknown>) => {
        window.clearTimeout(pending.timer);
        pendingRef.current.delete(id);
        pending.resolve(v);
      };
      if (!msg.ok) {
        done({ ok: false, error: String(msg.error || 'ошибка файловой операции') });
        return;
      }
      if (typeof msg.total === 'number' && typeof msg.data === 'string') {
        // чанк файла (get)
        if (!pending.chunks) pending.chunks = { total: Number(msg.total), parts: [] };
        pending.chunks.parts[Number(msg.seq)] = String(msg.data);
        if (pending.chunks.parts.filter((p) => typeof p === 'string').length >= pending.chunks.total) {
          done({ ok: true, name: String(msg.name || ''), size: Number(msg.size || 0), data: pending.chunks.parts.join('') });
        }
        return;
      }
      done(msg);
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [active, role, roomRef]);

  const request = useCallback((op: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const room = roomRef.current;
    if (!room) return Promise.resolve({ ok: false, error: 'нет соединения' });
    const id = (globalThis.crypto as Crypto | undefined)?.randomUUID?.() || `f${Date.now()}${Math.random()}`;
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        pendingRef.current.delete(id);
        resolve({ ok: false, error: 'хост не ответил (таймаут)' });
      }, REQUEST_TIMEOUT_MS);
      pendingRef.current.set(id, { resolve, timer });
      publish({ t: 'file_req', id, op, ...payload });
    });
  }, [roomRef, publish]);

  const requestFiles = useCallback(() => {
    publish({ t: 'file_ask' });
    log('info', 'удалённый доступ: запрошен доступ к файлам');
  }, [publish, log]);

  const enableFiles = useCallback(() => {
    setFilesRequested(false);
    setFilesOn(true);
    publish({ t: 'files', on: true });
    log('info', 'удалённый доступ: доступ к файлам разрешён (host)');
  }, [publish, log]);

  const denyFiles = useCallback(() => {
    setFilesRequested(false);
    publish({ t: 'files', on: false });
    log('info', 'удалённый доступ: доступ к файлам запрещён (host)');
  }, [publish, log]);

  const disableFiles = useCallback(() => {
    setFilesOn(false);
    publish({ t: 'files', on: false });
    log('info', 'удалённый доступ: доступ к файлам остановлен (host)');
  }, [publish, log]);

  const withBusy = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError('');
    try {
      return await fn();
    } catch (e) {
      setError(String((e as Error)?.message || e));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const list = useCallback(async (path: string) => {
    const r = await withBusy(() => request('list', { path }));
    if (!r || !r.ok) {
      setError(String(r?.error || 'не удалось получить список'));
      return null;
    }
    return {
      path: String(r.path || path),
      parent: String(r.parent || ''),
      entries: Array.isArray(r.entries) ? (r.entries as never[]) : [],
    };
  }, [request, withBusy]);

  const roots = useCallback(async () => {
    const r = await withBusy(() => request('roots', {}));
    if (!r || !r.ok) {
      setError(String(r?.error || 'не удалось получить диски'));
      return [];
    }
    return Array.isArray(r.roots) ? (r.roots as string[]) : [];
  }, [request, withBusy]);

  const getFile = useCallback(async (path: string) => {
    const r = await withBusy(() => request('get', { path }));
    if (!r || !r.ok) {
      setError(String(r?.error || 'не удалось скачать файл'));
      return null;
    }
    return { name: String(r.name || 'file'), size: Number(r.size || 0), data: String(r.data || '') };
  }, [request, withBusy]);

  const putFile = useCallback(async (path: string, b64: string) => {
    const total = Math.max(1, Math.ceil(b64.length / CHUNK_SIZE));
    const room = roomRef.current;
    if (!room) return false;
    setBusy(true);
    setError('');
    try {
      const id = (globalThis.crypto as Crypto | undefined)?.randomUUID?.() || `p${Date.now()}${Math.random()}`;
      const respPromise = new Promise<Record<string, unknown>>((resolve) => {
        const timer = window.setTimeout(() => {
          pendingRef.current.delete(id);
          resolve({ ok: false, error: 'хост не ответил (таймаут)' });
        }, REQUEST_TIMEOUT_MS);
        pendingRef.current.set(id, { resolve, timer });
      });
      publish({ t: 'file_req', id, op: 'put', path, total });
      for (let seq = 0; seq < total; seq++) {
        publish({ t: 'file_chunk', id, seq, total, data: b64.slice(seq * CHUNK_SIZE, (seq + 1) * CHUNK_SIZE) });
      }
      const r = await respPromise;
      if (!r.ok) {
        setError(String(r.error || 'не удалось загрузить файл'));
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  }, [roomRef, publish]);

  const remove = useCallback(async (path: string) => {
    const r = await withBusy(() => request('del', { path }));
    if (!r || !r.ok) {
      setError(String(r?.error || 'не удалось удалить файл'));
      return false;
    }
    return true;
  }, [request, withBusy]);

  return {
    filesOn, filesRequested, busy, error,
    requestFiles, enableFiles, denyFiles, disableFiles,
    roots, list, getFile, putFile, remove,
  };
}

export type RemoteFilesApi = ReturnType<typeof useRemoteFiles>;
